import { useCallback, useRef, useState } from 'react';

import { Icon, Popover, type IconName } from '@/components/ui';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';
import { useThemeStore, type Theme } from '@/stores/theme.store';
import { useUiStore, type NavMode } from '@/stores/ui.store';

/**
 * Appearance settings: where the navigation sits, and which theme.
 *
 * A small panel of labelled choices rather than two bare toggle buttons in the
 * bar, because a toggle only tells you what it will do next - here the current
 * setting is visible. Both preferences persist to localStorage.
 */
export function SettingsMenu() {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const navMode = useUiStore((state) => state.navMode);
  const setNavMode = useUiStore((state) => state.setNavMode);

  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useClickOutside([triggerRef, panelRef], close, open);
  useEscapeKey(() => {
    close();
    triggerRef.current?.focus();
  }, open);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Appearance settings"
        title="Appearance settings"
        onClick={() => {
          setOpen((previous) => !previous);
        }}
        className={cn(
          'rounded-sm p-2 text-content-muted transition-colors hover:bg-surface-muted hover:text-content sm:p-1.5',
          open && 'bg-surface-muted text-content',
        )}
      >
        <Icon name="settings" size={15} />
      </button>

      <Popover anchorRef={triggerRef} open={open} align="end" role="dialog" aria-label="Appearance">
        <div ref={panelRef} className="w-52 p-2">
          <OptionGroup<NavMode>
            label="Navigation"
            value={navMode}
            onChange={setNavMode}
            options={[
              { value: 'sidebar', label: 'Side', icon: 'layout-side' },
              { value: 'navbar', label: 'Top', icon: 'layout-top' },
            ]}
          />

          <div className="my-2 h-px bg-border" role="separator" />

          <OptionGroup<Theme>
            label="Theme"
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'light', label: 'Light', icon: 'sun' },
              { value: 'dark', label: 'Dark', icon: 'moon' },
            ]}
          />
        </div>
      </Popover>
    </>
  );
}

interface OptionGroupProps<T extends string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; icon: IconName }[];
}

/**
 * A segmented control. `radiogroup` rather than a row of buttons, so a screen
 * reader announces it as one setting with a chosen value.
 */
function OptionGroup<T extends string>({ label, value, onChange, options }: OptionGroupProps<T>) {
  return (
    <div>
      <p className="mb-1 text-2xs font-bold tracking-wide text-content-subtle uppercase">{label}</p>

      <div
        role="radiogroup"
        aria-label={label}
        className="flex rounded-sm border border-border-strong"
      >
        {options.map((option, index) => {
          const active = option.value === value;

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                onChange(option.value);
              }}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 text-xs transition-colors',
                index > 0 && 'border-l border-border-strong',
                active
                  ? 'chrome-button-primary font-bold text-primary-content'
                  : 'chrome-button text-content-muted hover:text-content',
              )}
            >
              <Icon name={option.icon} size={13} />
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
